import { Link } from 'react-router-dom';
import { Lock, CheckCircle, Dumbbell, ChevronRight } from 'lucide-react';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const WeeklyScheduleCard = ({
  program, workouts, selectedWeek, weekData, isEnrolled, currentWeek, hasAccess, onSelectWeek,
}) => (
  <div className="bg-card border border-border p-6">
    <h3 className="font-heading text-xl text-white uppercase tracking-wider mb-4">Weekly Schedule</h3>

    {/* Week Tabs */}
    <div className="flex flex-wrap gap-2 mb-6">
      {program.weeks?.map((week) => {
        const isCompleted = isEnrolled && week.week < currentWeek;
        const isLocked = !hasAccess || (isEnrolled && week.week > currentWeek);
        const isActive = selectedWeek === week.week;
        return (
          <button
            key={week.week}
            onClick={() => hasAccess && onSelectWeek(week.week)}
            disabled={!hasAccess}
            data-testid={`week-tab-${week.week}`}
            className={`px-4 py-2 font-heading text-sm uppercase tracking-wider transition-all flex items-center gap-2
              ${isActive
                ? 'bg-primary text-primary-foreground'
                : isCompleted
                  ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                  : !hasAccess
                    ? 'bg-card text-muted-foreground border border-border opacity-50 cursor-not-allowed'
                    : isLocked
                      ? 'bg-card text-muted-foreground border border-border opacity-50'
                      : 'bg-card text-white hover:bg-card/80 border border-border'}`}
          >
            {isCompleted && <CheckCircle className="h-3 w-3" />}
            {(!hasAccess || isLocked) && !isCompleted && <Lock className="h-3 w-3" />}
            Week {week.week}
          </button>
        );
      })}
    </div>

    {hasAccess && weekData ? (
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="font-heading text-lg text-primary uppercase tracking-wider">{weekData.focus}</h4>
            <p className="text-muted-foreground text-sm">
              {weekData.workouts?.length} workouts · {weekData.rest_days?.length} rest days
            </p>
          </div>
        </div>

        {/* Daily Schedule */}
        <div className="grid grid-cols-7 gap-2">
          {DAYS.map((day, i) => {
            const isRest = weekData.rest_days?.includes(i);
            // Find the workout type for this non-rest day. Days are filled in
            // order from the workouts[] array, skipping rest_days.
            const workoutIndex = weekData.workouts?.findIndex((_, idx) => {
              let dayCount = 0;
              for (let d = 0; d <= i; d++) {
                if (!weekData.rest_days?.includes(d)) {
                  if (dayCount === idx) return d === i;
                  dayCount++;
                }
              }
              return false;
            });
            const workoutType = !isRest && workoutIndex >= 0 ? weekData.workouts[workoutIndex] : null;

            return (
              <div key={day} className={`p-3 text-center ${
                isRest ? 'bg-black/50 border border-border' : 'bg-primary/10 border border-primary/30'
              }`}>
                <p className="text-xs text-muted-foreground uppercase mb-1">{day}</p>
                {isRest ? (
                  <p className="text-muted-foreground text-xs">Rest</p>
                ) : workoutType ? (
                  <div>
                    <Dumbbell className="h-4 w-4 text-primary mx-auto mb-1" />
                    <p className="text-white text-xs capitalize">{workoutType}</p>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        {/* Workout List for the Week */}
        <div className="mt-6 space-y-3">
          <h5 className="text-white font-medium">This Week's Workouts:</h5>
          {weekData.workouts?.map((category, i) => {
            const workout = workouts.find(w => w.category === category);
            if (!workout) return null;
            return (
              <Link
                key={i}
                to={`/workout/${workout.id}`}
                className="block bg-black border border-border p-4 hover:border-primary/50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 bg-primary/20 flex items-center justify-center">
                      <Dumbbell className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{workout.title}</p>
                      <p className="text-muted-foreground text-sm capitalize">{category} · {workout.duration_minutes} min</p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    ) : (
      <div className="text-center py-8">
        <Lock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">Purchase this program to unlock the full weekly schedule</p>
      </div>
    )}
  </div>
);
